import React, { useState, useEffect } from 'react';
import { pdf, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { FaSpinner } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import ClassifiedCard from './ClassifiedCard';

// Register normal Arial font
try {
  Font.register({
    family: 'Arial',
    fonts: [
      { src: '/fonts/arial.ttf', fontWeight: 200, fontStyle: 'normal' }, // Normal
      { src: '/fonts/ARIBL0.ttf', fontWeight: 500, fontStyle: 'normal' }, // Bold
    ],
  });
} catch (e) {
  console.warn("Arial font could not be loaded, falling back to default:", e);
}

// Page size in points (~33cm × 52cm)
const PAGE_WIDTH = 935.43;
const PAGE_HEIGHT = 1474.02;
const SAFE_MARGIN = 10;

const pdfStyles = StyleSheet.create({
  page: {
    fontFamily: 'Arial',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    padding: SAFE_MARGIN,
  },
  header: { fontSize: 24, fontWeight: 500, textAlign: 'center', marginBottom: 4 },
});

const uiStyles = {
  container: { width: '100%' },
  printButton: { cursor: 'pointer', transition: 'transform 0.2s' },
  error: { color: 'red', marginTop: '10px' },
};

// Sorting function (updated)
const sortData = (list) => {
  return [...list].sort((a, b) => {
    // 1. Height (in cm, default 10)
    const aHeight = a.size && !isNaN(parseFloat(a.size)) ? parseFloat(a.size) : 10;
    const bHeight = b.size && !isNaN(parseFloat(b.size)) ? parseFloat(b.size) : 10;

    if (aHeight !== bHeight) {
      return aHeight - bHeight; // smaller first
    }

    // 2. Category
    const catA = (a.category || '').toLowerCase();
    const catB = (b.category || '').toLowerCase();
    if (catA !== catB) {
      return catA.localeCompare(catB);
    }

    // 3. Space (Line ads first if needed)
    const spaceA = a.space === 'Line' ? 0 : 1;
    const spaceB = b.space === 'Line' ? 0 : 1;
    if (spaceA !== spaceB) return spaceA - spaceB;

    return 0;
  });
};


// PDF generator
const ClassifiedsDocument = ({ data }) => {
  // const sortedList = sortData(data);

  // Page settings
  const COLUMNS = 8;
  const COLUMN_WIDTH_CM = 3.8;
  const COLUMN_WIDTH_PT = COLUMN_WIDTH_CM * 28.346; // convert cm → pt
  const MAX_HEIGHT = PAGE_HEIGHT - 60; // minus header/footer
  const CARD_MARGIN_BOTTOM = 8;

  const pages = [];
  let currentPage = Array.from({ length: COLUMNS }, () => []);
  let colHeights = Array(COLUMNS).fill(0);

  data.forEach((item) => {
    const cmHeight = item.size && !isNaN(parseFloat(item.size)) ? parseFloat(item.size) : 10;
    const cardHeight = cmHeight * 28.346; // cm → pt
    const effectiveHeight = cardHeight + CARD_MARGIN_BOTTOM;

    let placed = false;
    for (let col = 0; col < COLUMNS; col++) {
      if (colHeights[col] + effectiveHeight <= MAX_HEIGHT) { // Changed to <= to allow exact fit; adjust if strict < needed
        currentPage[col].push(item);
        colHeights[col] += effectiveHeight;
        placed = true;
        break;
      }
    }

    if (!placed) {
      if (effectiveHeight > MAX_HEIGHT) {
        console.warn(`Card too tall to fit on any page: height ${effectiveHeight} > ${MAX_HEIGHT}`);
      }

      pages.push(currentPage);
      currentPage = Array.from({ length: COLUMNS }, () => []);
      colHeights = Array(COLUMNS).fill(0);

      currentPage[0].push(item);
      colHeights[0] = effectiveHeight;
    }
  });

  if (currentPage.some(col => col.length > 0)) {
    pages.push(currentPage);
  }

  return (
    <Document>
      {pages.map((pageCols, pageIndex) => (
        <Page
          key={pageIndex}
          size={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}
          style={pdfStyles.page}
        >
          {/* <link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap" rel="stylesheet" /> */}
          <Text style={pdfStyles.header}>Matrimonial Classifieds</Text>
          <View style={{ flexDirection: 'row-reverse' }}>
            {pageCols.map((colItems, colIndex) => (
              <View
                key={colIndex}
                style={{
                  width: COLUMN_WIDTH_PT,
                  marginRight: colIndex < COLUMNS - 1 ? 8 : 0,
                }}
              >
                {colItems.map((item, idx) => (
                  <View
                    key={item?.id || item?.crn || idx}
                    style={{ marginBottom: CARD_MARGIN_BOTTOM }}
                  >
                    <ClassifiedCard item={item} />
                  </View>
                ))}
              </View>
            ))}
          </View>
        </Page>
      ))}
    </Document>
  );
};

const MatrimonialClassifiedsPDF = ({ data, title = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line
  let cleanData = [];
  if (Array.isArray(data)) {
    cleanData = data.filter((item) => item && typeof item === 'object');
  } else if (data && typeof data === 'object') {
    cleanData = [data];
  }

  useEffect(() => {
    console.log('[MatrimonialClassifiedsPDF] data changed, items:', cleanData.length);
  }, [cleanData]);

  const createDocument = (pdfData) => <ClassifiedsDocument data={pdfData} />;

  const handleDownload = async () => {
    setError(null);
    if (!cleanData || cleanData.length === 0) {
      setError('No valid data to generate PDF.');
      return;
    }

    if (!pdf || typeof pdf !== 'function') {
      console.error('pdf() API is not available from @react-pdf/renderer.');
      setError('pdf() API is not available. Update @react-pdf/renderer to a recent version.');
      return;
    }

    setLoading(true);
    try {
      const docElement = createDocument(cleanData);
      const blob = await pdf(docElement).toBlob();
      if (!blob) throw new Error('Failed to create PDF blob (null).');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `matrimonial-classifieds.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      console.error('[MatrimonialClassifiedsPDF] PDF generation error:', err);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <div style={uiStyles.error}>Error: {error}</div>}
      <div style={uiStyles.container}>
        <button
          onClick={handleDownload}
          disabled={loading || cleanData.length === 0}
          className="text-white bg-green-600 hover:bg-green-700 border-green-600 p-1 rounded w-full"
          style={{ ...uiStyles.printButton, opacity: loading || cleanData.length === 0 ? 0.7 : 1 }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {loading ? (
            <span className="flex flex-wrap items-center gap-x-2">
              <FaSpinner size={18} /> {title}
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-x-2">
              <FiDownload size={18} /> {title}
            </span>
          )}
        </button>
      </div>
    </>
  );
};

export default MatrimonialClassifiedsPDF;